import { useEffect, lazy, Suspense } from 'react'
import { Toaster } from 'sonner'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from './contexts/AuthContext'
import { OrganizationProvider } from './contexts/OrganizationContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { useTheme } from './hooks/useTheme'
import { ProtectedRoute, ErrorBoundary } from './components/common'
import { ROLES } from './constants/roles'
import ProductRouteGuard from './components/ProductRouteGuard'
import DashboardLayout from './components/DashboardLayout'
import LoginPage from './pages/LoginPage'
import PostCreator from './components/PostCreator'

import { ResourcesPage } from './pages/ResourcesPage'
import { ResourceDetails } from './pages/ResourceDetails'
import FormsPage from './pages/FormsPage'
import UsersPage from './pages/UsersPage'
import WagerTeamPage from './pages/WagerTeamPage'
import SettingsPage from './pages/SettingsPage'
import NotificationsPage from './pages/NotificationsPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import AcceptInvitationPage from './pages/AcceptInvitationPage'
import CreatorManager from './components/CreatorManager'
import DesignWorkspacePage from './pages/DesignWorkspacePage'
import TagsPage from './pages/TagsPage'
import MediaGalleryPage from './pages/MediaGalleryPage'
import FeedOpsPage from './pages/FeedOpsPage'
import FeedPreviewPage from './pages/FeedPreviewPage'
import GeneratedPostsPage from './pages/GeneratedPostsPage'
import GrowthLabPage from './pages/GrowthLabPage'
import SchedulingPage from './pages/SchedulingPage'
import MediaTemplatesPage from './pages/MediaTemplatesPage'
import IGamingLayout from './pages/igaming/IGamingLayout'
import IGamingRulesPage from './pages/igaming/IGamingRulesPage'
import IGamingMerchandisingPage from './pages/igaming/IGamingMerchandisingPage'
import IGamingDataPage from './pages/igaming/IGamingDataPage'
import IGamingInsightsPage from './pages/igaming/IGamingInsightsPage'
import SportsSearchLayout from './pages/sports-search/SportsSearchLayout'
import { ARTIFACT_CHECK_TAB, INTEGRATION_PANEL, LAB_PATH, OVERVIEW_PATH, PANEL_PARAM, TAB_PARAM } from './pages/sports-search/sportsSearchPaths'
const SportsSearchOverviewPage = lazy(() => import('./pages/sports-search/SportsSearchOverviewPage'))
const SportsSearchLabPage = lazy(() => import('./pages/sports-search/SportsSearchLabPage'))
const SportsSearchAnalyticsPage = lazy(() => import('./pages/sports-search/SportsSearchAnalyticsPage'))
const SportsSearchDataPage = lazy(() => import('./pages/sports-search/SportsSearchDataPage'))
const SportsSearchRulesPage = lazy(() => import('./pages/sports-search/SportsSearchRulesPage'))
const SportsSearchFiltersPage = lazy(() => import('./pages/sports-search/SportsSearchFiltersPage'))
const SportsSearchWidgetPage = lazy(() => import('./pages/sports-search/SportsSearchWidgetPage'))
import NativeSportsbookPage from './pages/native-sportsbook/NativeSportsbookPage'
import NativeSportsbookThemePage from './pages/native-sportsbook/NativeSportsbookThemePage'
import PrepacksLayout from './pages/prepacks/PrepacksLayout'
import PrepacksLibraryPage from './pages/prepacks/PrepacksLibraryPage'
import PrepacksGeneratePage from './pages/prepacks/PrepacksGeneratePage'
import PrepacksSurfacesPage from './pages/prepacks/PrepacksSurfacesPage'
import PrepacksAutomationsLayout from './pages/prepacks/PrepacksAutomationsLayout'
import PrepacksBoostsPage from './pages/prepacks/PrepacksBoostsPage'
import PrepacksHistoryPage from './pages/prepacks/PrepacksHistoryPage'
import PrepacksUnscheduledPage from './pages/prepacks/PrepacksUnscheduledPage'
import PrepacksConfigurationPage from './pages/prepacks/PrepacksConfigurationPage'
import PrepacksGrowthPage from './pages/PrepacksGrowthPage'
const PrepacksSchedulesPage = lazy(() => import('./pages/prepacks/PrepacksSchedulesPage'))
const PrepacksMasterListPage = lazy(() => import('./pages/prepacks/PrepacksMasterListPage'))
const PrepackScheduleBuilderPage = lazy(loadScheduleBuilder)
import LiveLinksPage from './pages/affiliate-links/LiveLinksPage'
const CreateLinkPage = lazy(() => import('./pages/affiliate-links/CreateLinkPage'))
const AffiliatesPage = lazy(() => import('./pages/affiliate-links/AffiliatesPage'))
const MarketInsightsConfigurationPage = lazy(() => import('./pages/market-insights/MarketInsightsConfigurationPage'))
const MarketInsightsIntegrationPage = lazy(() => import('./pages/market-insights/MarketInsightsIntegrationPage'))
const AffiliateLinksSettingsPage = lazy(() => import('./pages/affiliate-links/AffiliateLinksSettingsPage'))
import CommandCenterLayout from './components/CommandCenterLayout'
import CommandCenterDashboard from './components/CommandCenterDashboard'
import CommandCenterPostList from './components/CommandCenterPostList'
import CommandCenterPostCreator from './components/CommandCenterPostCreator'
import CommandCenterPostGroupDetail from './components/CommandCenterPostGroupDetail'
import CommandCenterApprovals from './components/CommandCenterApprovals'
import CommandCenterCreators from './components/CommandCenterCreators'
import CommandCenterModerationPage from './pages/command-center/CommandCenterModerationPage'
import ModerationReviewsPage from './pages/moderation/ModerationReviewsPage'
import ModerationRulesPage from './pages/moderation/ModerationRulesPage'
import { useAuth } from './hooks/useAuth'
import { PRODUCT_KEYS } from './config/studioProducts'
import { studioDocumentTitle } from './config/studioRoutes'
import { useOrganization } from './hooks/useOrganization'
import { loadScheduleBuilder } from './pages/prepacks/prepackSchedulePaths'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30 * 1000,
    },
  },
})

function FeedOpsRedirect() {
  const { commandCenterAccess } = useAuth()

  if (commandCenterAccess) {
    return <Navigate to="/command-center/feed-ops" replace />
  }

  return <Navigate to="/dashboard/feed/posts" replace />
}

function FeedProductOutlet() {
  return (
    <ProductRouteGuard productKey={PRODUCT_KEYS.FEED}>
      <Outlet />
    </ProductRouteGuard>
  )
}

function IGamingProductOutlet() {
  return (
    <ProductRouteGuard productKey={PRODUCT_KEYS.IGAMING_SEARCH}>
      <Outlet />
    </ProductRouteGuard>
  )
}

function SportsSearchProductOutlet() {
  return (
    <ProductRouteGuard productKey={PRODUCT_KEYS.SPORTS_SEARCH}>
      <Outlet />
    </ProductRouteGuard>
  )
}

function NativeSportsbookProductOutlet() {
  return (
    <ProductRouteGuard productKey={PRODUCT_KEYS.NATIVE_SPORTSBOOK}>
      <Outlet />
    </ProductRouteGuard>
  )
}

function PrepacksProductOutlet() {
  return (
    <ProductRouteGuard productKey={PRODUCT_KEYS.PREPACKS}>
      <Outlet />
    </ProductRouteGuard>
  )
}

function MarketInsightsProductOutlet() {
  return (
    <ProductRouteGuard productKey={PRODUCT_KEYS.MARKET_INSIGHTS}>
      <Outlet />
    </ProductRouteGuard>
  )
}

function AffiliateLinksProductOutlet() {
  return (
    <ProductRouteGuard productKey={PRODUCT_KEYS.AFFILIATE_LINKS}>
      <Outlet />
    </ProductRouteGuard>
  )
}

// Keeps the query and hash; `params` are set on top of the query.
function LegacyRedirect({ to, params }) {
  const location = useLocation()
  if (!params) return <Navigate to={`${to}${location.search}${location.hash}`} replace />
  const searchParams = new URLSearchParams(location.search)
  for (const [key, value] of Object.entries(params)) searchParams.set(key, value)
  return <Navigate to={`${to}?${searchParams}${location.hash}`} replace />
}

// Integration moved into Overview's drawer and Quality became Search Lab's
// Artifact check tab (#729); keep old links working.
const SPORTS_SEARCH_INTEGRATION_PARAMS = { [PANEL_PARAM]: INTEGRATION_PANEL }
const SPORTS_SEARCH_QUALITY_PARAMS = { [TAB_PARAM]: ARTIFACT_CHECK_TAB }

function LegacyPersonalizedBetsRedirect() {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const search = searchParams.has('tab')
    ? location.search
    : `${location.search}${location.search ? '&' : '?'}tab=bets`

  return <Navigate to={`/dashboard/feed/generated-posts${search}${location.hash}`} replace />
}

function LegacyPostEditRedirect() {
  const { id } = useParams()
  const location = useLocation()
  return <Navigate to={`/dashboard/feed/posts/${id}/edit${location.search}${location.hash}`} replace />
}

function RouteTitleManager() {
  const location = useLocation()
  const { currentOrg } = useOrganization()

  useEffect(() => {
    document.title = studioDocumentTitle(location.pathname, currentOrg?.name)
  }, [currentOrg?.name, location.pathname])

  return null
}

// Toasts start below the app header, so they never cover it or the mobile
// menu button (styles/toasts.css sizes their close button).
const TOAST_OFFSET = { top: 'calc(var(--wf-app-header-height) + var(--wf-space-2))', right: 'var(--wf-space-4)' }
const TOAST_MOBILE_OFFSET = { ...TOAST_OFFSET, left: 'var(--wf-space-4)' }

function ThemedToaster() {
  const { resolvedTheme } = useTheme()
  return <Toaster position="top-right" richColors closeButton theme={resolvedTheme} offset={TOAST_OFFSET} mobileOffset={TOAST_MOBILE_OFFSET} />
}

function App() {

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <OrganizationProvider>
            <BrowserRouter>
              <RouteTitleManager />
              <ThemedToaster />
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard/feed/posts" replace />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/accept-invitation" element={<AcceptInvitationPage />} />

              {/* Command Center routes */}
              <Route
                path="/command-center"
                element={
                  <ProtectedRoute requireCommandCenterAccess>
                    <ErrorBoundary>
                      <CommandCenterLayout />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              >
                <Route index element={<CommandCenterDashboard />} />
                <Route path="posts" element={<CommandCenterPostList />} />
                <Route path="posts/new" element={<CommandCenterPostCreator />} />
                <Route path="post-groups/:id" element={<CommandCenterPostGroupDetail />} />
                <Route path="post-groups/:id/edit" element={<CommandCenterPostCreator mode="edit" />} />
                <Route path="approvals" element={<CommandCenterApprovals />} />
                <Route path="creators" element={<CommandCenterCreators />} />
                <Route path="moderation" element={<CommandCenterModerationPage />} />
                <Route path="feed-ops" element={<FeedOpsPage commandCenter />} />
                <Route
                  path="wager-team"
                  element={
                    <ProtectedRoute requiredGlobalRole={ROLES.SUPER_ADMIN}>
                      <WagerTeamPage />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <DashboardLayout />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard/feed/posts" replace />} />
                <Route path="feed" element={<FeedProductOutlet />}>
                  <Route index element={<Navigate to="posts" replace />} />
                  <Route path="posts" element={<FeedOpsPage />} />
                  <Route path="archived" element={<FeedOpsPage archivedOnly />} />
                  <Route
                    path="posts/scheduling"
                    element={
                      <ProtectedRoute requiredRole={ROLES.EDITOR}>
                        <SchedulingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="posts/templates"
                    element={
                      <ProtectedRoute requiredRole={ROLES.EDITOR}>
                        <MediaTemplatesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="generated-posts"
                    element={
                      <ProtectedRoute requiredRole={ROLES.INTERNAL_ORG_ADMIN}>
                        <ProductRouteGuard
                          productKey={PRODUCT_KEYS.FEED}
                          action="publish"
                          title="Publishing access required"
                          description="This organization does not have Feed publishing access."
                        >
                          <GeneratedPostsPage />
                        </ProductRouteGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="personalized-bets" element={<LegacyPersonalizedBetsRedirect />} />
                  <Route
                    path="posts/new"
                    element={
                      <ProtectedRoute requiredRole={ROLES.EDITOR}>
                        <PostCreator />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="posts/:id/edit"
                    element={
                      <ProtectedRoute requiredRole={ROLES.EDITOR}>
                        <PostCreator mode="edit" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="preview"
                    element={
                      <ProtectedRoute requiredRole={ROLES.INTERNAL_ORG_ADMIN}>
                        <FeedPreviewPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="configuration"
                    element={
                      <ProtectedRoute requiredRole={ROLES.INTERNAL_ORG_ADMIN}>
                        <ProductRouteGuard
                          productKey={PRODUCT_KEYS.FEED}
                          action="configure"
                          title="Configuration access required"
                          description="This organization does not have Feed configuration access."
                        >
                          <DesignWorkspacePage />
                        </ProductRouteGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="design" element={<LegacyRedirect to="/dashboard/feed/configuration" />} />
                  <Route
                    path="moderation"
                    element={
                      <ProtectedRoute requiredRole={ROLES.EXTERNAL_ORG_ADMIN}>
                        <ProductRouteGuard
                          productKey={PRODUCT_KEYS.FEED}
                          action="publish"
                          title="Publishing access required"
                          description="Deciding moderation reviews requires Feed publishing access."
                        >
                          <ModerationReviewsPage />
                        </ProductRouteGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="moderation/rules"
                    element={
                      <ProtectedRoute requiredRole={ROLES.EXTERNAL_ORG_ADMIN}>
                        <ProductRouteGuard
                          productKey={PRODUCT_KEYS.FEED}
                          action="configure"
                          title="Configuration access required"
                          description="Editing phrase rules requires Feed configuration access."
                        >
                          <ModerationRulesPage />
                        </ProductRouteGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="tags"
                    element={
                      <ProtectedRoute requiredRole={ROLES.EDITOR}>
                        <TagsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="assets" element={<MediaGalleryPage />} />
                  <Route
                    path="growth"
                    element={
                      <ProductRouteGuard
                        productKey={PRODUCT_KEYS.FEED}
                        action="analyze"
                        title="Analytics access required"
                        description="This account can open Wager Feed, but does not have analytics access for this product."
                      >
                        <GrowthLabPage />
                      </ProductRouteGuard>
                    }
                  />
                </Route>
                <Route path="posts" element={<LegacyRedirect to="/dashboard/feed/posts" />} />
                <Route path="posts/new" element={<LegacyRedirect to="/dashboard/feed/posts/new" />} />
                <Route path="posts/:id/edit" element={<LegacyPostEditRedirect />} />
                <Route path="feed-ops" element={<FeedOpsRedirect />} />
                <Route path="feed-preview" element={<LegacyRedirect to="/dashboard/feed/preview" />} />
                <Route path="design" element={<LegacyRedirect to="/dashboard/feed/configuration" />} />
                <Route path="feed-design" element={<LegacyRedirect to="/dashboard/feed/configuration" />} />
                <Route path="feed-settings" element={<LegacyRedirect to="/dashboard/feed/configuration" />} />
                <Route path="feeds" element={<LegacyRedirect to="/dashboard/feed/configuration" />} />
                <Route path="tags" element={<LegacyRedirect to="/dashboard/feed/tags" />} />
                <Route path="assets" element={<LegacyRedirect to="/dashboard/feed/assets" />} />
                <Route path="media" element={<LegacyRedirect to="/dashboard/feed/assets" />} />
                <Route
                  path="creators"
                  element={
                    <ProductRouteGuard productKey={PRODUCT_KEYS.FEED}>
                      <CreatorManager />
                    </ProductRouteGuard>
                  }
                />
                <Route path="igaming" element={<IGamingProductOutlet />}>
                  <Route element={<IGamingLayout />}>
                    <Route index element={<Navigate to="/dashboard/igaming/rules" replace />} />
                    <Route path="rules" element={<IGamingRulesPage />} />
                    <Route path="merchandising" element={<IGamingMerchandisingPage />} />
                    <Route path="data" element={<IGamingDataPage />} />
                    <Route
                      path="insights"
                      element={
                        <ProductRouteGuard
                          productKey={PRODUCT_KEYS.IGAMING_SEARCH}
                          action="analyze"
                          title="Analytics access required"
                          description="This account can open iGaming, but does not have analytics access for this product."
                        >
                          <IGamingInsightsPage />
                        </ProductRouteGuard>
                      }
                    />
                    <Route path="promotions" element={<Navigate to="/dashboard/igaming/merchandising" replace />} />
                    <Route path="jackpots" element={<Navigate to="/dashboard/igaming/merchandising" replace />} />
                    <Route path="metadata" element={<Navigate to="/dashboard/igaming/data" replace />} />
                    <Route path="sources" element={<Navigate to="/dashboard/igaming/data" replace />} />
                  </Route>
                </Route>
                <Route path="sports-search" element={<SportsSearchProductOutlet />}>
                  <Route element={<SportsSearchLayout />}>
                    <Route index element={<Navigate to={OVERVIEW_PATH} replace />} />
                    <Route path="overview" element={<Suspense fallback={null}><SportsSearchOverviewPage /></Suspense>} />
                    <Route path="search-lab" element={<Suspense fallback={null}><SportsSearchLabPage /></Suspense>} />
                    <Route path="integration" element={<LegacyRedirect to={OVERVIEW_PATH} params={SPORTS_SEARCH_INTEGRATION_PARAMS} />} />
                    <Route
                      path="filters"
                      element={
                        <ProductRouteGuard
                          productKey={PRODUCT_KEYS.SPORTS_SEARCH}
                          action="configure"
                          title="Configure access required"
                          description="Managing search filters requires configure access."
                        >
                          <Suspense fallback={null}><SportsSearchFiltersPage /></Suspense>
                        </ProductRouteGuard>
                      }
                    />
                    {/* Read-only without configure access; the page says so. */}
                    <Route path="widget" element={<Suspense fallback={null}><SportsSearchWidgetPage /></Suspense>} />
                    <Route
                      path="analytics"
                      element={
                        <ProductRouteGuard
                          productKey={PRODUCT_KEYS.SPORTS_SEARCH}
                          action="analyze"
                          title="Analytics access required"
                          description="This account can open Sports Search, but does not have analytics access for this product."
                        >
                          <Suspense fallback={null}><SportsSearchAnalyticsPage /></Suspense>
                        </ProductRouteGuard>
                      }
                    />
                    <Route path="quality" element={<LegacyRedirect to={LAB_PATH} params={SPORTS_SEARCH_QUALITY_PARAMS} />} />
                    <Route
                      path="data"
                      element={
                        <ProtectedRoute requiredRole={ROLES.INTERNAL_ORG_ADMIN}>
                          <ProductRouteGuard
                            productKey={PRODUCT_KEYS.SPORTS_SEARCH}
                            action="operate"
                            title="Operate access required"
                            description="Internal Sports Search data operations require operate access."
                          >
                            <Suspense fallback={null}><SportsSearchDataPage /></Suspense>
                          </ProductRouteGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="settings"
                      element={
                        <ProductRouteGuard
                          productKey={PRODUCT_KEYS.SPORTS_SEARCH}
                          action="configure"
                          title="Configure access required"
                          description="Sports Search settings require configure access."
                        >
                          <Suspense fallback={null}><SportsSearchRulesPage /></Suspense>
                        </ProductRouteGuard>
                      }
                    />
                  </Route>
                </Route>
                <Route path="native-sportsbook" element={<NativeSportsbookProductOutlet />}>
                  <Route index element={<Navigate to="/dashboard/native-sportsbook/manifests" replace />} />
                  <Route path="manifests" element={<NativeSportsbookPage />} />
                  <Route path="theme" element={<NativeSportsbookThemePage />} />
                </Route>
                <Route path="prepacks" element={<PrepacksProductOutlet />}>
                  <Route element={<PrepacksLayout />}>
                    {/* Library left the sidebar (#1056); Schedules is the one tab every Prepacks role can open. */}
                    <Route index element={<Navigate to="/dashboard/prepacks/automations/schedules" replace />} />
                    <Route path="library" element={<PrepacksLibraryPage />} />
                    <Route path="recipes" element={<Navigate to="/dashboard/prepacks/automations/schedules" replace />} />
                    <Route
                      path="generate"
                      element={
                        <ProtectedRoute requiredRole={ROLES.EDITOR}>
                          <ProductRouteGuard
                            productKey={PRODUCT_KEYS.PREPACKS}
                            action="configure"
                            title="Create access required"
                            description="Generating prepacks requires configure access to Prepacks."
                          >
                            <PrepacksGeneratePage />
                          </ProductRouteGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="surfaces"
                      element={
                        <ProtectedRoute requiredRole={ROLES.INTERNAL_ORG_ADMIN}>
                          <ProductRouteGuard
                            productKey={PRODUCT_KEYS.PREPACKS}
                            action="configure"
                            title="Surfaces access required"
                            description="Managing Prepacks surfaces requires configure access."
                          >
                            <PrepacksSurfacesPage />
                          </ProductRouteGuard>
                        </ProtectedRoute>
                      }
                    />
                    {/* The schedule builder is a full page beside the Schedules hub, not
                        inside it. Both routes render the same tree, so a create that
                        replaces /new with /:id keeps the editor mounted. */}
                    <Route
                      path="automations/schedules/new"
                      element={
                        <ProductRouteGuard
                          productKey={PRODUCT_KEYS.PREPACKS}
                          action="configure"
                          title="Configure access required"
                          description="Creating schedules requires configure access to Prepacks."
                        >
                          <Suspense fallback={null}>
                            <PrepackScheduleBuilderPage />
                          </Suspense>
                        </ProductRouteGuard>
                      }
                    />
                    <Route
                      path="automations/schedules/:id"
                      element={
                        <ProductRouteGuard productKey={PRODUCT_KEYS.PREPACKS} action="view">
                          <Suspense fallback={null}>
                            <PrepackScheduleBuilderPage />
                          </Suspense>
                        </ProductRouteGuard>
                      }
                    />
                    <Route path="automations" element={<PrepacksAutomationsLayout />}>
                      <Route path="master-list" element={
                        <ProtectedRoute requiredGlobalRole={ROLES.SUPER_ADMIN}>
                          <Suspense fallback={null}><PrepacksMasterListPage /></Suspense>
                        </ProtectedRoute>
                      } />
                      <Route index element={<Navigate to="/dashboard/prepacks/automations/schedules" replace />} />
                      <Route
                        path="boosts"
                        element={
                          <ProtectedRoute requiredRole={ROLES.INTERNAL_ORG_ADMIN}>
                            <ProductRouteGuard
                              productKey={PRODUCT_KEYS.PREPACKS}
                              action="operate"
                              title="Boosts access required"
                              description="Managing Prepacks schedules requires operate access."
                            >
                              <PrepacksBoostsPage />
                            </ProductRouteGuard>
                          </ProtectedRoute>
                        }
                      />
                      <Route path="recipes" element={<Navigate to="/dashboard/prepacks/automations/schedules" replace />} />
                      <Route
                        path="schedules"
                        element={
                          <Suspense fallback={null}>
                            <PrepacksSchedulesPage />
                          </Suspense>
                        }
                      />
                      {/* The packs no live schedule made (#1080): view access, like Schedules. */}
                      <Route
                        path="unscheduled"
                        element={
                          <ProductRouteGuard productKey={PRODUCT_KEYS.PREPACKS} action="view">
                            <PrepacksUnscheduledPage />
                          </ProductRouteGuard>
                        }
                      />
                      <Route
                        path="history"
                        element={
                          <ProtectedRoute requiredRole={ROLES.INTERNAL_ORG_ADMIN}>
                            <ProductRouteGuard
                              productKey={PRODUCT_KEYS.PREPACKS}
                              action="operate"
                              title="History access required"
                              description="Managing Prepacks schedules requires operate access."
                            >
                              <PrepacksHistoryPage />
                            </ProductRouteGuard>
                          </ProtectedRoute>
                        }
                      />
                    </Route>
                    <Route
                      path="configuration"
                      element={
                        <ProtectedRoute requiredRole={ROLES.INTERNAL_ORG_ADMIN}>
                          <ProductRouteGuard
                            productKey={PRODUCT_KEYS.PREPACKS}
                            action="configure"
                            title="Templates access required"
                            description="Managing Prepacks templates requires configure access."
                          >
                            <PrepacksConfigurationPage />
                          </ProductRouteGuard>
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                  <Route
                    path="growth"
                    element={
                      <ProtectedRoute requiredRole={ROLES.INTERNAL_ORG_ADMIN}>
                        <ProductRouteGuard
                          productKey={PRODUCT_KEYS.PREPACKS}
                          action="analyze"
                          title="Growth access required"
                          description="Prepacks Growth requires analyze access to Prepacks."
                        >
                          <PrepacksGrowthPage />
                        </ProductRouteGuard>
                      </ProtectedRoute>
                    }
                  />
                </Route>
                <Route path="market-insights" element={<MarketInsightsProductOutlet />}>
                  <Route index element={<Navigate to="/dashboard/market-insights/configuration" replace />} />
                  <Route path="configuration" element={<Suspense fallback={null}><MarketInsightsConfigurationPage /></Suspense>} />
                  <Route path="integration" element={<Suspense fallback={null}><MarketInsightsIntegrationPage /></Suspense>} />
                </Route>
                <Route path="affiliate-links" element={<AffiliateLinksProductOutlet />}>
                  <Route index element={<Navigate to="/dashboard/affiliate-links/live" replace />} />
                  <Route path="live" element={<LiveLinksPage />} />
                  <Route
                    path="create"
                    element={
                      <ProductRouteGuard
                        productKey={PRODUCT_KEYS.AFFILIATE_LINKS}
                        action="publish"
                        title="Link creation access required"
                        description="Creating marketing links requires publish access to Affiliate links."
                      >
                        <Suspense fallback={null}>
                          <CreateLinkPage />
                        </Suspense>
                      </ProductRouteGuard>
                    }
                  />
                  <Route
                    path="affiliates"
                    element={
                      <Suspense fallback={null}>
                        <AffiliatesPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <Suspense fallback={null}>
                        <AffiliateLinksSettingsPage />
                      </Suspense>
                    }
                  />
                </Route>
                <Route path="analytics" element={<LegacyRedirect to="/dashboard/feed/growth" />} />
                <Route path="experiments" element={
                  <ProtectedRoute requiredRole="internal_org_admin"><Navigate to="/dashboard/feed/growth?tab=tests" replace /></ProtectedRoute>
                } />
                <Route
                  path="resources"
                  element={
                    <ProtectedRoute requiredPermission="manage_resources">
                      <ResourcesPage />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="resources/:id" 
                  element={
                    <ProtectedRoute requiredPermission="manage_resources">
                      <ResourceDetails />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="forms" 
                  element={
                    <ProtectedRoute requiredPermission="manage_forms">
                      <FormsPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="users" 
                  element={
                    <ProtectedRoute requiredPermission="manage_users">
                      <UsersPage />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="notifications"
                  element={
                    <ProtectedRoute requiredRole={ROLES.SUPER_ADMIN}>
                      <NotificationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
              
              <Route path="/admin" element={<Navigate to="/dashboard/feed/posts" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </OrganizationProvider>
      </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
